// Customer Library modal. Two tabs: My designs (reopen a saved design in the
// current builder) and My uploads (drag artwork back onto the canvas without
// re-uploading).
//
// Interaction model:
//   - Click a thumbnail to add/open it immediately (no "Use" / "Open" buttons).
//   - Every item carries an always-visible checkbox top-left for multi-select.
//   - The top-right of the header carries the bulk actions: Place + Delete
//     on the uploads tab, Archive + Delete on the designs tab. They operate
//     on the currently-checked items; disabled when nothing is selected.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../lib/api'

export interface SavedDesign {
  id: string; kind: 'garment' | 'dtf'; name: string | null; status: string
  priceCents: number | null; proofUrl: string | null
  createdAt: string; updatedAt: string
}
export interface SavedUpload {
  id: string; originalName: string; mimeType: string; size: number
  widthPx: number | null; heightPx: number | null
  url: string; createdAt: string
}

interface Props {
  open: boolean
  onClose: () => void
  kind: 'garment' | 'dtf'
  onOpenDesign: (d: SavedDesign) => void
  onUseUpload: (u: SavedUpload) => void
}

export default function LibraryDrawer({ open, onClose, kind, onOpenDesign, onUseUpload }: Props) {
  const { user: authkitUser, getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  // Server-side sign-in mirror. AuthKit React's `user` can be null during
  // the in-browser hydration handshake even when the server-side sealed
  // cookie is valid (e.g. right after a /auth/login -> /auth/callback
  // redirect lands the customer on /designer or /dtf). Without this the
  // drawer would flash "Sign in to save designs" to a customer who is
  // very much signed in.
  const [serverSignedIn, setServerSignedIn] = useState<boolean | null>(null)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d: { authenticated?: boolean }) => { if (!cancelled) setServerSignedIn(!!d?.authenticated) })
      .catch(() => { if (!cancelled) setServerSignedIn(false) })
    return () => { cancelled = true }
  }, [open])
  const signedIn = !!authkitUser || serverSignedIn === true
  // Default to Uploads — that's the bucket the customer most often opens
  // the library for (re-using a logo or artwork). Designs are the second
  // tab.
  const [tab, setTab] = useState<'designs' | 'uploads'>('uploads')
  const [designs, setDesigns] = useState<SavedDesign[]>([])
  const [uploads, setUploads] = useState<SavedUpload[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Multi-select state — one set per tab so flipping tabs preserves a partial
  // selection on the other.
  const [selDesigns, setSelDesigns] = useState<Set<string>>(new Set())
  const [selUploads, setSelUploads] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  // Upload state — drives the button busy spinner + drag-overlay highlight.
  const [uploadBusy, setUploadBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dragCounter = useRef(0)

  async function load() {
    if (!signedIn) return
    setLoading(true)
    try {
      const [d, u] = await Promise.all([
        apiFetch<{ items: SavedDesign[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/designs'),
        apiFetch<{ items: SavedUpload[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/uploads'),
      ])
      setDesigns(d.items); setUploads(u.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    if (!open) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signedIn])

  // Reset selection state every time the drawer is closed so reopening
  // starts fresh.
  useEffect(() => {
    if (!open) { setSelDesigns(new Set()); setSelUploads(new Set()) }
  }, [open])

  function toggleDesign(id: string) {
    setSelDesigns((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }
  function toggleUpload(id: string) {
    setSelUploads((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  async function bulkArchiveDesigns() {
    if (selDesigns.size === 0) return
    const ids = Array.from(selDesigns)
    if (!confirm(`Archive ${ids.length} design${ids.length === 1 ? '' : 's'}? They'll be removed from your library but kept for order history.`)) return
    setBulkBusy(true)
    setDesigns((prev) => prev.filter((d) => !selDesigns.has(d.id)))
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/designs/bulk-delete', {
        method: 'POST', body: JSON.stringify({ ids, hard: false }),
      })
    } catch (e) { alert((e as Error).message); await load() }
    finally { setBulkBusy(false); setSelDesigns(new Set()) }
  }
  async function bulkDeleteDesigns() {
    if (selDesigns.size === 0) return
    const ids = Array.from(selDesigns)
    if (!confirm(`Delete ${ids.length} design${ids.length === 1 ? '' : 's'} forever? This cannot be undone.`)) return
    setBulkBusy(true)
    setDesigns((prev) => prev.filter((d) => !selDesigns.has(d.id)))
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/designs/bulk-delete', {
        method: 'POST', body: JSON.stringify({ ids, hard: true }),
      })
    } catch (e) { alert((e as Error).message); await load() }
    finally { setBulkBusy(false); setSelDesigns(new Set()) }
  }
  async function renameDesign(id: string, name: string) {
    const trimmed = name.trim()
    if (!trimmed) { setRenamingId(null); return }
    setDesigns((prev) => prev.map((d) => d.id === id ? { ...d, name: trimmed } : d))
    setRenamingId(null)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/designs/${id}`, {
        method: 'PATCH', body: JSON.stringify({ name: trimmed }),
      })
    } catch (e) { alert((e as Error).message); await load() }
  }

  async function bulkDeleteUploads() {
    if (selUploads.size === 0) return
    const ids = Array.from(selUploads)
    if (!confirm(`Delete ${ids.length} upload${ids.length === 1 ? '' : 's'} forever? This cannot be undone.`)) return
    setBulkBusy(true)
    setUploads((prev) => prev.filter((u) => !selUploads.has(u.id)))
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/uploads/bulk-delete', {
        method: 'POST', body: JSON.stringify({ ids }),
      })
    } catch (e) { alert((e as Error).message); await load() }
    finally { setBulkBusy(false); setSelUploads(new Set()) }
  }
  function bulkPlaceUploads() {
    if (selUploads.size === 0) return
    // Walk the selection in insertion order — onUseUpload drops each at the
    // canvas's standard placement position. Customer can move them after.
    const toPlace = uploads.filter((u) => selUploads.has(u.id))
    toPlace.forEach((u) => onUseUpload(u))
    setSelUploads(new Set())
    onClose()
  }

  // Probe an image's natural dimensions before upload so the file row carries
  // the px size (needed for true-to-size DTF preview + admin DPI grading).
  function probeDims(dataUrl: string): Promise<{ w: number; h: number }> {
    return new Promise((resolve) => {
      const probe = new Image()
      probe.onload = () => resolve({ w: probe.naturalWidth, h: probe.naturalHeight })
      probe.onerror = () => resolve({ w: 0, h: 0 })
      probe.src = dataUrl
    })
  }

  async function uploadOne(file: File) {
    if (file.size > 25 * 1024 * 1024) throw new Error(`${file.name}: max 25 MB`)
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
    const dims = await probeDims(dataUrl)
    const up = await apiFetch<{ id: string; url: string }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
      method: 'POST',
      body: JSON.stringify({
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64: dataUrl,
        kind: 'artwork',
        widthPx: dims.w || undefined,
        heightPx: dims.h || undefined,
      }),
    })
    return { id: up.id, url: up.url, dims }
  }

  // Upload one or many files from the file picker / drag-drop. Surfaces a
  // running count so the customer knows the batch is in flight.
  async function uploadFiles(files: FileList | File[]) {
    const imgs = Array.from(files).filter((f) => f.type.startsWith('image/'))
    if (imgs.length === 0) {
      alert('Only image files (PNG / JPG / SVG / WebP) can be uploaded.')
      return
    }
    setUploadBusy(true)
    setUploadProgress({ done: 0, total: imgs.length })
    const fresh: SavedUpload[] = []
    try {
      for (let i = 0; i < imgs.length; i++) {
        const f = imgs[i]!
        try {
          const up = await uploadOne(f)
          fresh.push({
            id: up.id, originalName: f.name, mimeType: f.type, size: f.size,
            widthPx: up.dims.w || null, heightPx: up.dims.h || null,
            url: up.url, createdAt: new Date().toISOString(),
          })
        } catch (e) { console.warn('upload failed:', (e as Error).message) }
        setUploadProgress({ done: i + 1, total: imgs.length })
      }
      if (fresh.length === 0) throw new Error('No files uploaded successfully.')
      // Prepend so the new uploads are immediately visible at the top.
      setUploads((prev) => [...fresh, ...prev])
    } catch (e) { alert((e as Error).message) }
    finally { setUploadBusy(false); setUploadProgress(null) }
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const fs = e.target.files
    if (fs && fs.length) void uploadFiles(fs)
    e.target.value = ''
  }

  // Drag-and-drop handlers. The drag-counter pattern prevents the overlay
  // from flickering when the cursor crosses child elements inside the
  // drop zone (dragenter / dragleave fire per element, not just the root).
  function onDragEnter(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation()
    if (!e.dataTransfer.types.includes('Files')) return
    dragCounter.current += 1
    setIsDragging(true)
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current === 0) setIsDragging(false)
  }
  function onDragOver(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files && files.length) void uploadFiles(files)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="eyebrow !mb-0">My Library</div>
            <h2 className="font-heading uppercase font-bold text-lg text-ink">Saved designs &amp; uploads</h2>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-2xl leading-none">×</button>
        </div>

        {/* Show the sign-in nudge ONLY when we've definitively confirmed the
            user is signed out (server mirror returned authenticated:false AND
            no AuthKit user). While the server mirror is still resolving we
            render the loading state below so a signed-in customer doesn't
            see a phantom "sign in" message. */}
        {serverSignedIn === null && !authkitUser ? (
          <div className="p-6 text-center text-sm text-ink-3">Loading your library…</div>
        ) : !signedIn ? (
          <div className="p-6 text-center text-sm text-ink-3">Sign in to save designs and reuse uploads across sessions.</div>
        ) : (
          <>
            {/* Tabs + bulk action buttons (top-right). The buttons operate
                on whatever is currently checked; disabled when nothing is
                selected. Uploads tab shows Place + Delete; Designs tab
                shows Archive + Delete. */}
            <div className="flex items-center justify-between px-6 pt-3 border-b border-border shrink-0 gap-3 flex-wrap">
              <div className="flex gap-1">
                <button onClick={() => setTab('uploads')} className={'px-3 py-2 text-xs font-semibold uppercase tracking-wide ' + (tab === 'uploads' ? 'border-b-2 border-gold text-ink' : 'text-ink-3 hover:text-ink')}>Uploads ({uploads.length})</button>
                <button onClick={() => setTab('designs')} className={'px-3 py-2 text-xs font-semibold uppercase tracking-wide ' + (tab === 'designs' ? 'border-b-2 border-gold text-ink' : 'text-ink-3 hover:text-ink')}>Designs ({designs.length})</button>
              </div>
              {tab === 'uploads' ? (
                <div className="flex items-center gap-2 pb-2">
                  <input ref={fileInputRef} type="file" accept="image/*" multiple
                    onChange={onPickFiles} className="hidden" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadBusy}
                    className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded bg-ink text-white hover:bg-ink-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
                    title="Upload images from this device (or drag-drop them onto the grid)">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    {uploadBusy
                      ? `Uploading${uploadProgress ? ` ${uploadProgress.done}/${uploadProgress.total}` : ''}…`
                      : 'Upload'}
                  </button>
                  <button
                    onClick={bulkPlaceUploads}
                    disabled={selUploads.size === 0 || bulkBusy}
                    className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded bg-gold text-ink hover:bg-gold-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title="Place all checked uploads onto the canvas">
                    Place{selUploads.size > 0 ? ` (${selUploads.size})` : ''}
                  </button>
                  <button
                    onClick={() => void bulkDeleteUploads()}
                    disabled={selUploads.size === 0 || bulkBusy}
                    className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded border border-red-600 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title="Delete all checked uploads forever">
                    Delete{selUploads.size > 0 ? ` (${selUploads.size})` : ''}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 pb-2">
                  <button
                    onClick={() => void bulkArchiveDesigns()}
                    disabled={selDesigns.size === 0 || bulkBusy}
                    className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded border border-ink text-ink hover:bg-ink hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title="Archive selected designs (kept for order history)">
                    Archive{selDesigns.size > 0 ? ` (${selDesigns.size})` : ''}
                  </button>
                  <button
                    onClick={() => void bulkDeleteDesigns()}
                    disabled={selDesigns.size === 0 || bulkBusy}
                    className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded border border-red-600 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title="Permanently delete selected designs">
                    Delete{selDesigns.size > 0 ? ` (${selDesigns.size})` : ''}
                  </button>
                </div>
              )}
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {err && <div className="card text-red-700 mb-3">{err}</div>}
              {loading && <div className="text-sm text-ink-3">Loading…</div>}

              {tab === 'designs' && !loading && (
                <div className="space-y-3">
                  {designs.length === 0 && <p className="text-sm text-ink-3">No saved designs yet. Use the Save button at the top of the builder to add one.</p>}
                  {designs.map((d) => {
                    const isSel = selDesigns.has(d.id)
                    const canOpen = d.kind === kind
                    const isRenaming = renamingId === d.id
                    return (
                      <div key={d.id}
                        className={'border rounded p-3 flex gap-3 items-center ' +
                          (isSel ? 'border-gold-dark ring-2 ring-gold ' : 'border-border ') +
                          (canOpen ? '' : 'opacity-60')}>
                        {/* Checkbox */}
                        <input type="checkbox" checked={isSel} onChange={() => toggleDesign(d.id)}
                          className="w-4 h-4 accent-gold cursor-pointer shrink-0"
                          title="Select for bulk action" />
                        {/* Thumbnail — click to open */}
                        <button
                          onClick={() => canOpen && onOpenDesign(d)}
                          disabled={!canOpen}
                          className="w-16 h-16 bg-surface border border-border rounded overflow-hidden flex items-center justify-center shrink-0 hover:border-gold disabled:cursor-not-allowed transition-colors"
                          title={canOpen ? 'Open design' : `${d.kind} designs only`}>
                          {d.proofUrl ? <img src={apiSrc(d.proofUrl)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] uppercase text-ink-3">{d.kind}</span>}
                        </button>
                        {/* Name + meta */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">{d.kind} · {d.status}</div>
                          {isRenaming ? (
                            <input
                              autoFocus
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); void renameDesign(d.id, renameValue) }
                                if (e.key === 'Escape') setRenamingId(null)
                              }}
                              onBlur={() => { if (renameValue.trim()) void renameDesign(d.id, renameValue); else setRenamingId(null) }}
                              className="w-full text-sm font-semibold text-ink border-b border-gold focus:outline-none bg-transparent pb-0.5"
                              maxLength={120}
                            />
                          ) : (
                            <button
                              onClick={() => canOpen && onOpenDesign(d)}
                              disabled={!canOpen}
                              className="text-sm font-semibold text-ink hover:text-gold-dark transition-colors text-left truncate block w-full disabled:cursor-not-allowed">
                              {d.name ?? 'Untitled design'}
                            </button>
                          )}
                          <div className="text-[11px] text-ink-3 mt-0.5">{new Date(d.updatedAt).toLocaleDateString()}</div>
                        </div>
                        {/* Rename button */}
                        {!isRenaming && (
                          <button
                            onClick={() => { setRenamingId(d.id); setRenameValue(d.name ?? '') }}
                            className="shrink-0 p-1.5 text-ink-3 hover:text-ink rounded transition-colors"
                            title="Rename design">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        )}
                        {!canOpen && <span className="text-[11px] text-ink-3 italic shrink-0">{d.kind} only</span>}
                      </div>
                    )
                  })}
                </div>
              )}

              {tab === 'uploads' && !loading && (
                <div
                  className={'relative rounded transition-colors ' + (isDragging ? 'bg-gold/5 ring-2 ring-gold ring-offset-2 ring-offset-white' : '')}
                  onDragEnter={onDragEnter}
                  onDragLeave={onDragLeave}
                  onDragOver={onDragOver}
                  onDrop={onDrop}>
                  {isDragging && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                      <div className="bg-white border-2 border-dashed border-gold rounded-lg px-6 py-4 shadow-lg text-center">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gold-dark">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <div className="text-sm font-semibold uppercase tracking-wider text-ink mt-2">Drop to upload</div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-1">
                    {uploads.length === 0 && (
                      <div className="col-span-full border-2 border-dashed border-border rounded p-10 text-center text-sm text-ink-3">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-ink-3/60 mb-3">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        No uploaded artwork yet.<br />
                        <span className="text-[11px]">Drop image files here, or hit <strong className="text-ink-2">Upload</strong> above.</span>
                      </div>
                    )}
                  {uploads.map((u) => {
                    const isSel = selUploads.has(u.id)
                    return (
                      <div key={u.id}
                        className={'relative border rounded overflow-hidden bg-surface ' +
                          (isSel ? 'border-gold-dark ring-2 ring-gold' : 'border-border')}>
                        {/* Always-visible checkbox in top-left */}
                        <input type="checkbox" checked={isSel} onChange={() => toggleUpload(u.id)}
                          className="absolute top-1.5 left-1.5 w-4 h-4 accent-gold cursor-pointer z-10 shadow-sm bg-white"
                          title="Select for bulk action"
                          onClick={(e) => e.stopPropagation()} />
                        {/* Click the thumbnail area to add it to the canvas */}
                        <button onClick={() => { onUseUpload(u); onClose() }}
                          className="block w-full text-left group">
                          <div className="aspect-square flex items-center justify-center bg-white group-hover:bg-gold/5 transition-colors">
                            <img src={apiSrc(u.url)} alt={u.originalName} className="max-w-full max-h-full object-contain pointer-events-none" />
                          </div>
                          <div className="p-2 group-hover:bg-gold/5 transition-colors">
                            <div className="text-[11px] text-ink truncate" title={u.originalName}>{u.originalName}</div>
                            <div className="text-[10px] text-ink-3 group-hover:text-gold-dark">Click to add to canvas</div>
                          </div>
                        </button>
                      </div>
                    )
                  })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
